import { CircularProgress, Stack, Typography } from '@mui/material'
import { useCurrentStore, useDeliveryQuote, useOrdering } from 'hooks'
import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { roundM } from 'services'

interface OrderDeliveryInfoProps {
  onError?: (message?: string) => void
}

export const OrderDeliveryInfo = ({ onError }: OrderDeliveryInfoProps) => {
  const { t } = useTranslation()
  const { formatTime, formatCurrency } = useCurrentStore()
  const { store } = useCurrentStore()
  const { order, saveOrderProps, isAutoFillDeliveryFee } = useOrdering()

  const { createDeliveryQuote, isLoading, deliveryQuote, errorMessage } =
    useDeliveryQuote()

  const orderType = order?.order_type
  const deliveryPlace = order?.customer_place
  const orderTime = order?.timeIsoFormat
  const isASAP = order?.is_asap
  // Create delivery quote based on delivery place
  useEffect(() => {
    if (!store || !isAutoFillDeliveryFee) return

    if (
      orderType !== 'delivery' ||
      (deliveryPlace?.place_id === undefined &&
        deliveryPlace?.saved_address_id === undefined &&
        (isASAP === true || orderTime === undefined))
    ) {
      // If we dont' have either delivery address or (in case of later) expected time,
      // we can't create a delivery quote and thus we need to clear the existing one
      if (order?.delivery_quote) {
        saveOrderProps({
          delivery_fee_amount: undefined,
          delivery_quote: undefined,
        })
      }
      onError?.()
    } else if (
      deliveryPlace?.place_id !== order?.delivery_quote?.place_id ||
      (isASAP !== true &&
        orderTime !== order?.delivery_quote?.expected_dropoff) ||
      deliveryPlace?.saved_address_id
    ) {
      // If we have delivery address and (in case of later) expected time,
      // wecreate a delivery quote
      const destination = deliveryPlace?.description ?? ''
      const orderTotal = order?.total ?? 0
      createDeliveryQuote(
        destination,
        orderTotal,
        isASAP === true ? undefined : orderTime,
      )
    }
    // Otherwise just do nothing
  }, [orderType, deliveryPlace, isASAP, orderTime, isAutoFillDeliveryFee]) // eslint-disable-line react-hooks/exhaustive-deps
  // The above line lint check is disabled to avoid cirular dependency

  useEffect(() => {
    if (!isEmpty(errorMessage)) {
      if (order?.delivery_quote) {
        saveOrderProps({
          delivery_fee_amount: undefined,
          delivery_quote: undefined,
        })
      }
      onError?.(errorMessage)
    }
  }, [errorMessage]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!deliveryQuote || !order || order.order_type !== 'delivery') return

    const deliveryFee = roundM((deliveryQuote?.fee ?? 0) / 100)

    saveOrderProps({
      delivery_fee_amount: deliveryFee,
      delivery_quote: {
        ...deliveryQuote,
        place_id: deliveryPlace?.place_id,
        expected_dropoff: orderTime,
      },
    })
    onError?.()
  }, [deliveryQuote, order?.order_type]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <CircularProgress size={40} sx={{ mt: 2 }} />
  }

  return (
    <Stack spacing={1} mt={2}>
      {errorMessage && (
        <Typography variant="h5" color="error">
          {t(errorMessage)}
        </Typography>
      )}
      {order?.delivery_fee_amount !== undefined && (
        <Typography variant="body2" color="secondary">
          <Trans
            defaults="Estimated delivery fee: <strong>{{amount}}</strong>."
            values={{
              amount: formatCurrency(order.delivery_fee_amount),
            }}
          />
        </Typography>
      )}
      {order?.delivery_quote?.dropoff_eta && (
        <Typography variant="body2" color="secondary">
          <Trans
            defaults="Estimated delivery at: <strong>{{time}}</strong>."
            values={{
              time: formatTime(order?.delivery_quote?.dropoff_eta),
            }}
          />
        </Typography>
      )}
    </Stack>
  )
}
